"use client";
import { Merchant } from "@/dto/merchant";
import { CartItem } from "@/models/cart/types";
import {
  ROUTE_ALL,
  ROUTE_MENU_ALL,
  ROUTE_SERVICES_ALL,
} from "@/utils/constants";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { LocalStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import clsx from "clsx";
import Link from "next/link";
import React, { useEffect, useMemo, useState } from "react";
import { useFormatter, useTranslations } from "next-intl";

interface DeliveryEstimateBannerProps {
  merchant: Merchant;
  // eslint-disable-next-line no-unused-vars
  onAddMore: (value?: boolean) => void;
}

export const DeliveryEstimateBanner = ({
  merchant,
  onAddMore,
}: DeliveryEstimateBannerProps) => {
  const t = useTranslations("DeliveryEstimateBanner");
  const format = useFormatter();

  const [totalPrice, setTotalPrice] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    // Determine storage key based on merchant type to avoid always defaulting to RETAIL_CART
    const storageKey = merchant.isRestaurant
      ? LocalStorageKey.RESTAURANT_CART
      : LocalStorageKey.RETAIL_CART;

    const cartItems = getFromLocalStorage<{ value: CartItem }[]>(
      storageKey,
      []
    );
    const total =
      cartItems.reduce((total: number, i: any) => total + i.value.total, 0) /
      100;

    setTotalPrice(total);
  }, [merchant.isRestaurant]);

  const url = useMemo(() => {
    if (merchant.isService) {
      return ROUTE_SERVICES_ALL;
    } else if (merchant.isRestaurant) {
      return ROUTE_MENU_ALL;
    } else {
      return ROUTE_ALL;
    }
  }, [merchant]);

  if (!isMounted) return null;

  const deliveryFee = merchant.deliveryFee ?? 0;
  const deliveryOrderNoFeesAmount = merchant.deliveryOrderNoFeesAmount ?? 0;
  const deliveryMinimum = merchant.deliveryMinimum ?? 0;

  const hasMetMinimumFreeDeliveryPrice =
    deliveryOrderNoFeesAmount / 100 <= totalPrice;

  const hasMetMinimumDelivery = deliveryMinimum / 100 <= totalPrice;

  const amountToFreeDelivery = format.number(
    deliveryOrderNoFeesAmount / 100 - totalPrice,
    {
      style: "currency",
      currency: merchant.defaultCurrency,
      currencyDisplay: "symbol",
    }
  );

  const minimumDeliveryAmount = format.number(deliveryMinimum / 100, {
    style: "currency",
    currency: merchant.defaultCurrency,
    currencyDisplay: "symbol",
  });

  const renderLink = (chunks: React.ReactNode) => (
    <>
      <br />
      <Link
        href={url}
        onClick={() => onAddMore?.()}
        className="underline cursor-pointer text-[#0764AB]"
        data-testid={applyDataTestId(
          hasMetMinimumDelivery
            ? pom.deliveryEstimateBanner.addToCart
            : pom.deliveryEstimateBanner.minimumDelivery
        )}
      >
        {chunks}
      </Link>
    </>
  );

  const showFreeDelivery = hasMetMinimumFreeDeliveryPrice && deliveryFee >= 0;
  const showAwayFromFree =
    deliveryFee > 0 && hasMetMinimumDelivery && !hasMetMinimumFreeDeliveryPrice;
  const showMinimumNotMet = !hasMetMinimumDelivery;

  // Don't render if none of the conditions for the inner text are met.
  if (!showFreeDelivery && !showAwayFromFree && !showMinimumNotMet) {
    return null;
  }

  return (
    <div
      className={clsx("rounded-lg border border-solid", {
        "bg-[#eefbfc] border-[#69D0DD]": hasMetMinimumDelivery,
        "bg-[#FFF6CC] border-[#FFD100]": !hasMetMinimumDelivery,
        "p-4": deliveryFee >= 0 || !hasMetMinimumDelivery,
      })}
    >
      {showFreeDelivery && (
        <p
          className="font-medium text-base"
          data-testid={applyDataTestId(pom.deliveryEstimateBanner.freeDelivery)}
        >
          {t("freeDelivery")}
        </p>
      )}

      {showAwayFromFree && (
        <p
          className="font-medium text-base"
          data-testid={applyDataTestId(
            pom.deliveryEstimateBanner.awayFromFreeDelivery
          )}
        >
          {t.rich("awayFromFreeDelivery", {
            amount: amountToFreeDelivery,
            link: renderLink,
          })}
        </p>
      )}

      {showMinimumNotMet && (
        <p className="font-normal text-base">
          {t.rich("minimumForDelivery", {
            amount: minimumDeliveryAmount,
            link: renderLink,
          })}
        </p>
      )}
    </div>
  );
};
